import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Boxes, ClipboardList, Truck, Building2, AlertTriangle, Plus, Trash2,
  History, ChevronRight, CheckCircle2, XCircle,
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "accounts", label: "Palettenkonten", icon: Boxes },
  { key: "transports", label: "Transportaufträge", icon: Truck },
  { key: "carriers", label: "Spediteure", icon: Building2 },
];

const fmtN = (v) => Number(v || 0).toLocaleString("de-DE");
const fmtDate = (v) => v ? new Date(v).toLocaleDateString("de-DE") : "—";

export default function Pallets() {
  const [tab, setTab] = useState("accounts");
  const [summary, setSummary] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [transports, setTransports] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [detail, setDetail] = useState(null); // account detail dialog
  const [movementDlg, setMovementDlg] = useState(null); // {party_type, party_id, party_name}
  const [transportDlg, setTransportDlg] = useState(null); // create/edit
  const [carrierDlg, setCarrierDlg] = useState(null);
  const [statusFilter, setStatusFilter] = useState("planned");
  const [params] = useSearchParams();

  const load = async () => {
    try {
      const [s, a, t, c] = await Promise.all([
        api.get("/pallets/summary"),
        api.get("/pallets/accounts"),
        api.get("/transports"),
        api.get("/carriers"),
      ]);
      setSummary(s.data);
      setAccounts(a.data);
      setTransports(t.data);
      setCarriers(c.data);
    } catch (err) {
      toast.error("Palettendaten konnten nicht geladen werden");
    }
  };

  useEffect(() => {
    load();
    api.get("/customers?page_size=500").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/delivery-notes?page_size=100").then((r) => setDeliveries(r.data)).catch(() => {});
  }, []);

  // Deep-link: /paletten?customer=<id> öffnet direkt das Konto-Detail
  useEffect(() => {
    const cid = params.get("customer");
    if (cid && accounts.length) {
      const a = accounts.find((x) => x.party_type === "customer" && x.party_id === cid);
      if (a) openAccount(a);
    }
    // eslint-disable-next-line
  }, [params, accounts.length]);

  const openAccount = async (a) => {
    try {
      const { data } = await api.get(`/pallets/accounts/${a.party_type}/${a.party_id}`);
      setDetail(data);
    } catch { toast.error("Detail konnte nicht geladen werden"); }
  };

  const filteredTransports = useMemo(() => {
    if (statusFilter === "all") return transports;
    return transports.filter((t) => t.status === statusFilter);
  }, [transports, statusFilter]);

  return (
    <div data-testid="page-pallets">
      <PageHeader
        eyebrow="Warehouse"
        title="Palettenverwaltung"
        description="Palettenkonten pro Kunde, Palettentausch beim Warenausgang, Transportaufträge für Rücktransporte."
      />

      {/* Summary KPIs */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-[var(--border)] rounded-2xl p-5" data-testid="kpi-open-pallets">
            <div className="overline text-[var(--text-muted)]">Offene Paletten (gesamt)</div>
            <div className="font-display font-black text-3xl mt-1">{fmtN(summary.total_open_pallets)}</div>
          </div>
          <div className="bg-white border border-[var(--border)] rounded-2xl p-5" data-testid="kpi-debt-customers">
            <div className="overline text-[var(--text-muted)]">Kunden mit Palettenschuld</div>
            <div className="font-display font-black text-3xl mt-1">{summary.customers_with_debt}</div>
          </div>
          <div className={`border rounded-2xl p-5 ${summary.over_threshold_count > 0 ? "bg-red-50 border-red-300" : "bg-white border-[var(--border)]"}`} data-testid="kpi-transport-alert">
            <div className={`overline inline-flex items-center gap-1 ${summary.over_threshold_count > 0 ? "text-red-700" : "text-[var(--text-muted)]"}`}>
              <AlertTriangle size={12} /> Über Schwelle ({fmtN(summary.threshold)})
            </div>
            <div className={`font-display font-black text-3xl mt-1 ${summary.over_threshold_count > 0 ? "text-red-700" : ""}`}>
              {summary.over_threshold_count}
            </div>
          </div>
          <div className="bg-white border border-[var(--border)] rounded-2xl p-5" data-testid="kpi-planned-transports">
            <div className="overline text-[var(--text-muted)]">Geplante Transporte</div>
            <div className="font-display font-black text-3xl mt-1">{summary.planned_transports}</div>
          </div>
        </div>
      )}

      {/* Threshold-warning banner */}
      {summary?.over_threshold?.length > 0 && (
        <div className="mb-6 rounded-2xl border-2 border-red-200 bg-red-50 p-4 flex items-start gap-3" data-testid="banner-threshold">
          <div className="h-10 w-10 rounded-xl bg-red-500 text-white flex items-center justify-center shrink-0">
            <Truck size={20} />
          </div>
          <div className="flex-1">
            <div className="font-display font-black text-lg text-red-900">
              LKW-Transport organisieren: {summary.over_threshold.length} Kunde{summary.over_threshold.length === 1 ? "" : "n"} ≥ {fmtN(summary.threshold)} Paletten offen
            </div>
            <div className="text-xs text-red-800 mt-1">
              {summary.over_threshold.map((c) => `${c.party_name} (${fmtN(c.open_balance)})`).join(" · ")}
            </div>
          </div>
          <Button size="sm" variant="outline"
                  className="rounded-full border-red-300 text-red-700"
                  data-testid="btn-open-transport-tab"
                  onClick={() => setTab("transports")}>
            Transporte öffnen <ChevronRight size={14} className="ml-1" />
          </Button>
        </div>
      )}

      {/* Tab bar */}
      <div className="flex items-center gap-2 border-b border-[var(--border)] mb-6" data-testid="pallets-tabs">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
                  data-testid={`tab-${key}`}
                  className={`inline-flex items-center gap-2 px-4 py-2 border-b-2 text-sm font-semibold transition-colors ${
                    tab === key
                      ? "border-[var(--pop-pink)] text-[var(--pop-black)]"
                      : "border-transparent text-[var(--text-muted)] hover:text-[var(--pop-black)]"
                  }`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === "accounts" && (
        <AccountsTab accounts={accounts} customers={customers}
                     onOpen={openAccount}
                     onNewMovement={(p) => setMovementDlg(p)} />
      )}
      {tab === "transports" && (
        <TransportsTab transports={filteredTransports}
                       statusFilter={statusFilter} setStatusFilter={setStatusFilter}
                       carriers={carriers} customers={customers} deliveries={deliveries}
                       onCreate={() => setTransportDlg({ mode: "create" })}
                       onEdit={(t) => setTransportDlg({ mode: "edit", ...t })}
                       reload={load} />
      )}
      {tab === "carriers" && (
        <CarriersTab carriers={carriers}
                     onCreate={() => setCarrierDlg({ mode: "create" })}
                     onEdit={(c) => setCarrierDlg({ mode: "edit", ...c })}
                     reload={load} />
      )}

      {/* Konto-Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-account-detail">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Palettenkonto — {detail?.party_name}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl border p-3">
                  <div className="overline">Geliefert</div>
                  <div className="font-display font-black text-2xl">{fmtN(detail.delivered_total)}</div>
                </div>
                <div className="rounded-xl border p-3">
                  <div className="overline">Zurück</div>
                  <div className="font-display font-black text-2xl">{fmtN(detail.returned_total)}</div>
                </div>
                <div className={`rounded-xl border p-3 ${detail.open_balance > 0 ? "bg-red-50 border-red-300" : "bg-green-50 border-green-300"}`}>
                  <div className="overline">Offen</div>
                  <div className={`font-display font-black text-2xl ${detail.open_balance > 0 ? "text-red-700" : "text-green-700"}`}
                       data-testid="account-detail-balance">
                    {fmtN(detail.open_balance)}
                  </div>
                </div>
              </div>

              {detail.needs_transport && (
                <div className="rounded-xl border-2 border-red-300 bg-red-50 p-3 text-sm text-red-900 flex items-center gap-2" data-testid="detail-transport-hint">
                  <Truck size={16} /> Schwelle {fmtN(detail.threshold)} erreicht — LKW-Transport organisieren.
                  <Button size="sm" className="ml-auto rounded-full bg-red-600 text-white hover:bg-red-700"
                          data-testid="btn-create-transport-from-detail"
                          onClick={() => {
                            setDetail(null);
                            setTab("transports");
                            setTransportDlg({ mode: "create", customer_id: detail.party_id,
                                              pallet_count: detail.open_balance });
                          }}>
                    Transport anlegen
                  </Button>
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">Bewegungen</div>
                <Button size="sm" variant="outline" className="rounded-full"
                        data-testid="btn-new-movement"
                        onClick={() => setMovementDlg({
                          party_type: detail.party_type,
                          party_id: detail.party_id,
                          party_name: detail.party_name,
                        })}>
                  <Plus size={14} className="mr-1" /> Buchung
                </Button>
              </div>

              <div className="max-h-[40vh] overflow-y-auto border rounded-xl">
                <Table className="text-xs">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="overline">Datum</TableHead>
                      <TableHead className="overline">Typ</TableHead>
                      <TableHead className="overline">Referenz</TableHead>
                      <TableHead className="overline text-right">Geliefert</TableHead>
                      <TableHead className="overline text-right">Zurück</TableHead>
                      <TableHead className="overline">Notiz</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.ledger.map((m) => (
                      <TableRow key={m.id}>
                        <TableCell className="font-mono">{fmtDate(m.created_at)}</TableCell>
                        <TableCell>{m.ref_type}</TableCell>
                        <TableCell className="font-mono">{m.ref_number || (m.ref_id || "").slice(-6) || "—"}</TableCell>
                        <TableCell className="text-right font-mono">{m.delivered ? `+${m.delivered}` : ""}</TableCell>
                        <TableCell className="text-right font-mono text-green-700">{m.returned ? `−${m.returned}` : ""}</TableCell>
                        <TableCell className="truncate max-w-xs">{m.note || ""}</TableCell>
                      </TableRow>
                    ))}
                    {detail.ledger.length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center py-6 text-[var(--text-muted)]">Noch keine Buchungen.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Movement Dialog */}
      {movementDlg && (
        <MovementDialog
          initial={movementDlg}
          customers={customers}
          onClose={() => setMovementDlg(null)}
          onSaved={() => { setMovementDlg(null); load(); if (detail) openAccount(detail); }}
        />
      )}

      {/* Transport Dialog */}
      {transportDlg && (
        <TransportDialog
          initial={transportDlg}
          customers={customers}
          carriers={carriers}
          deliveries={deliveries}
          onClose={() => setTransportDlg(null)}
          onSaved={() => { setTransportDlg(null); load(); }}
        />
      )}

      {/* Carrier Dialog */}
      {carrierDlg && (
        <CarrierDialog
          initial={carrierDlg}
          onClose={() => setCarrierDlg(null)}
          onSaved={() => { setCarrierDlg(null); load(); }}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Accounts Tab                                                               */
/* -------------------------------------------------------------------------- */
function AccountsTab({ accounts, customers, onOpen, onNewMovement }) {
  const [q, setQ] = useState("");
  const filtered = accounts.filter((a) => !q ||
    (a.party_name || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <Card className="p-0 overflow-hidden rounded-2xl">
      <div className="p-3 border-b flex items-center justify-between gap-3">
        <Input placeholder="Kunde / Lieferant filtern…" value={q}
               data-testid="input-account-search"
               onChange={(e) => setQ(e.target.value)} className="max-w-md h-9 rounded-full" />
        <NewAccountButton customers={customers} onCreate={onNewMovement} />
      </div>
      <Table className="table-zebra">
        <TableHeader>
          <TableRow>
            <TableHead className="overline">Konto</TableHead>
            <TableHead className="overline">Typ</TableHead>
            <TableHead className="overline text-right">Geliefert</TableHead>
            <TableHead className="overline text-right">Zurück</TableHead>
            <TableHead className="overline text-right">Offen</TableHead>
            <TableHead className="overline">Letzte Bewegung</TableHead>
            <TableHead className="overline text-right">Aktion</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((a) => (
            <TableRow key={`${a.party_type}-${a.party_id}`}
                      data-testid={`account-row-${a.party_id}`}>
              <TableCell className="font-medium">{a.party_name}</TableCell>
              <TableCell>
                <span className={`pop-pill text-[10px] font-bold ${a.party_type === "customer" ? "bg-[#dbeafe] text-[#1e3a8a]" : "bg-[#fef3c7] text-[#854d0e]"}`}>
                  {a.party_type === "customer" ? "Kunde" : "Lieferant"}
                </span>
              </TableCell>
              <TableCell className="text-right font-mono">{fmtN(a.delivered_total)}</TableCell>
              <TableCell className="text-right font-mono text-green-700">−{fmtN(a.returned_total)}</TableCell>
              <TableCell className={`text-right font-mono font-bold ${a.open_balance > 0 ? "text-red-700" : a.open_balance < 0 ? "text-blue-700" : ""}`}>
                {fmtN(a.open_balance)}
              </TableCell>
              <TableCell className="font-mono text-xs">{fmtDate(a.last_movement)}</TableCell>
              <TableCell className="text-right">
                <button className="text-xs font-semibold hover:underline inline-flex items-center gap-1"
                        data-testid={`btn-open-account-${a.party_id}`}
                        onClick={() => onOpen(a)}>
                  <History size={12} /> Konto
                </button>
              </TableCell>
            </TableRow>
          ))}
          {filtered.length === 0 && (
            <TableRow><TableCell colSpan={7} className="text-center py-8 text-[var(--text-muted)]">
              Keine Palettenkonten. Erste Buchung über „Neue Buchung" anlegen.
            </TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Card>
  );
}

function NewAccountButton({ customers, onCreate }) {
  return (
    <Button className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold"
            data-testid="btn-new-account-movement"
            onClick={() => onCreate({ party_type: "customer", party_id: "", party_name: "" })}>
      <Plus size={14} className="mr-1" /> Neue Buchung
    </Button>
  );
}

/* -------------------------------------------------------------------------- */
/* Transports Tab                                                             */
/* -------------------------------------------------------------------------- */
function TransportsTab({ transports, statusFilter, setStatusFilter, onCreate, onEdit, reload }) {
  const setStatus = async (id, status) => {
    try { await api.patch(`/transports/${id}`, { status }); toast.success("Status aktualisiert"); reload(); }
    catch { toast.error("Fehler"); }
  };
  const del = async (id) => {
    if (!window.confirm("Transportauftrag wirklich löschen?")) return;
    try { await api.delete(`/transports/${id}`); toast.success("Gelöscht"); reload(); }
    catch { toast.error("Fehler"); }
  };
  return (
    <Card className="p-0 overflow-hidden rounded-2xl">
      <div className="p-3 border-b flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 text-xs">
          {["planned", "in_transit", "completed", "cancelled", "all"].map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)}
                    data-testid={`filter-transport-${s}`}
                    className={`px-3 py-1.5 rounded-full font-semibold ${statusFilter === s ? "bg-[var(--pop-pink)] text-white" : "bg-[var(--pop-cream)]/50 text-[var(--text-muted)]"}`}>
              {s === "all" ? "Alle" : s === "planned" ? "Geplant" : s === "in_transit" ? "Unterwegs" : s === "completed" ? "Abgeschlossen" : "Storniert"}
            </button>
          ))}
        </div>
        <Button className="ml-auto rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold"
                data-testid="btn-new-transport" onClick={onCreate}>
          <Plus size={14} className="mr-1" /> Neuer Transportauftrag
        </Button>
      </div>
      <Table className="table-zebra">
        <TableHeader>
          <TableRow>
            <TableHead className="overline">Nr.</TableHead>
            <TableHead className="overline">Kunde</TableHead>
            <TableHead className="overline">Abholung</TableHead>
            <TableHead className="overline text-right">Paletten</TableHead>
            <TableHead className="overline">Fahrer / Spediteur</TableHead>
            <TableHead className="overline">Ziel</TableHead>
            <TableHead className="overline">Status</TableHead>
            <TableHead className="overline text-right">Aktion</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {transports.map((t) => (
            <TableRow key={t.id} data-testid={`transport-row-${t.transport_number}`}>
              <TableCell className="font-mono font-bold">{t.transport_number}</TableCell>
              <TableCell>{t.customer_name}</TableCell>
              <TableCell className="font-mono text-xs">{fmtDate(t.pickup_date)}</TableCell>
              <TableCell className="text-right font-mono">{fmtN(t.pallet_count)}</TableCell>
              <TableCell className="text-xs">
                {t.driver_name && <div>{t.driver_name}</div>}
                {t.carrier_name && <div className="text-[var(--text-muted)]">{t.carrier_name}</div>}
                {!t.driver_name && !t.carrier_name && "—"}
              </TableCell>
              <TableCell className="text-xs">
                {t.destination_name} · {t.destination_zip} {t.destination_city}
              </TableCell>
              <TableCell>
                <StatusPill status={t.status} />
              </TableCell>
              <TableCell className="text-right whitespace-nowrap">
                {t.status === "planned" && (
                  <button onClick={() => setStatus(t.id, "in_transit")}
                          data-testid={`btn-transport-start-${t.transport_number}`}
                          className="text-xs font-semibold text-blue-700 hover:underline mr-3">
                    Start
                  </button>
                )}
                {(t.status === "planned" || t.status === "in_transit") && (
                  <button onClick={() => setStatus(t.id, "completed")}
                          data-testid={`btn-transport-complete-${t.transport_number}`}
                          className="text-xs font-semibold text-green-700 hover:underline mr-3 inline-flex items-center gap-1">
                    <CheckCircle2 size={12} /> Abschließen
                  </button>
                )}
                <button onClick={() => onEdit(t)}
                        data-testid={`btn-transport-edit-${t.transport_number}`}
                        className="text-xs font-semibold hover:underline mr-3">Bearbeiten</button>
                <button onClick={() => del(t.id)}
                        data-testid={`btn-transport-delete-${t.transport_number}`}
                        className="text-xs font-semibold text-red-700 hover:underline">
                  <Trash2 size={12} className="inline mr-1" />
                </button>
              </TableCell>
            </TableRow>
          ))}
          {transports.length === 0 && (
            <TableRow><TableCell colSpan={8} className="text-center py-8 text-[var(--text-muted)]">
              Keine Transportaufträge.
            </TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Card>
  );
}

function StatusPill({ status }) {
  const map = {
    planned: { label: "Geplant", cls: "bg-[#dbeafe] text-[#1e3a8a]" },
    in_transit: { label: "Unterwegs", cls: "bg-[#fef3c7] text-[#854d0e]" },
    completed: { label: "Abgeschlossen", cls: "bg-[#d1fae5] text-[#065f46]" },
    cancelled: { label: "Storniert", cls: "bg-[#fee2e2] text-[#991b1b]" },
  };
  const m = map[status] || { label: status, cls: "bg-gray-100 text-gray-700" };
  return <span className={`pop-pill text-[10px] font-bold ${m.cls}`}>{m.label}</span>;
}

/* -------------------------------------------------------------------------- */
/* Carriers Tab                                                               */
/* -------------------------------------------------------------------------- */
function CarriersTab({ carriers, onCreate, onEdit, reload }) {
  const del = async (id) => {
    if (!window.confirm("Spediteur wirklich löschen?")) return;
    try { await api.delete(`/carriers/${id}`); toast.success("Gelöscht"); reload(); }
    catch { toast.error("Fehler"); }
  };
  return (
    <Card className="p-0 overflow-hidden rounded-2xl">
      <div className="p-3 border-b flex items-center justify-end">
        <Button className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold"
                data-testid="btn-new-carrier" onClick={onCreate}>
          <Plus size={14} className="mr-1" /> Neuer Spediteur
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="overline">Name</TableHead>
            <TableHead className="overline">Kontakt</TableHead>
            <TableHead className="overline">Telefon</TableHead>
            <TableHead className="overline">Email</TableHead>
            <TableHead className="overline text-right">Aktion</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {carriers.map((c) => (
            <TableRow key={c.id} data-testid={`carrier-row-${c.id}`}>
              <TableCell className="font-medium">{c.name}</TableCell>
              <TableCell>{c.contact_name || "—"}</TableCell>
              <TableCell className="font-mono text-xs">{c.phone || "—"}</TableCell>
              <TableCell className="font-mono text-xs">{c.email || "—"}</TableCell>
              <TableCell className="text-right whitespace-nowrap">
                <button onClick={() => onEdit(c)} className="text-xs font-semibold hover:underline mr-3"
                        data-testid={`btn-carrier-edit-${c.id}`}>Bearbeiten</button>
                <button onClick={() => del(c.id)} className="text-xs font-semibold text-red-700 hover:underline"
                        data-testid={`btn-carrier-delete-${c.id}`}>
                  <Trash2 size={12} className="inline mr-1" /> Löschen
                </button>
              </TableCell>
            </TableRow>
          ))}
          {carriers.length === 0 && (
            <TableRow><TableCell colSpan={5} className="text-center py-8 text-[var(--text-muted)]">
              Kein Spediteur eingetragen.
            </TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Movement dialog                                                            */
/* -------------------------------------------------------------------------- */
function MovementDialog({ initial, customers, onClose, onSaved }) {
  const [f, setF] = useState({
    party_type: initial.party_type || "customer",
    party_id: initial.party_id || "",
    delivered: 0, returned: 0, note: "",
  });
  const save = async () => {
    if (!f.party_id) return toast.error("Bitte Kunde/Lieferant wählen");
    if (!f.delivered && !f.returned) return toast.error("Bitte Geliefert oder Zurück eintragen");
    try {
      await api.post("/pallets/movements", {
        party_type: f.party_type, party_id: f.party_id,
        delivered: Number(f.delivered || 0), returned: Number(f.returned || 0),
        note: f.note || null,
      });
      toast.success("Buchung erfasst"); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="dialog-movement">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Palettenbuchung</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Typ</Label>
            <Select value={f.party_type} onValueChange={(v) => setF({ ...f, party_type: v, party_id: "" })}>
              <SelectTrigger data-testid="select-movement-party-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="customer">Kunde</SelectItem>
                <SelectItem value="supplier">Lieferant</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{f.party_type === "customer" ? "Kunde" : "Lieferant"}</Label>
            <Select value={f.party_id} onValueChange={(v) => setF({ ...f, party_id: v })}
                    disabled={!!initial.party_id}>
              <SelectTrigger data-testid="select-movement-party"><SelectValue placeholder="Wählen…" /></SelectTrigger>
              <SelectContent>
                {(f.party_type === "customer" ? customers : []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {f.party_type === "supplier" && !initial.party_id && (
              <div className="text-xs text-[var(--text-muted)] mt-1">
                Lieferanten-Auswahl vorerst manuell — ID direkt eingeben oder aus Lieferanten-Modul öffnen.
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Geliefert (+)</Label>
              <Input type="number" min="0" value={f.delivered} data-testid="input-movement-delivered"
                     onChange={(e) => setF({ ...f, delivered: e.target.value })} />
            </div>
            <div>
              <Label>Zurück (−)</Label>
              <Input type="number" min="0" value={f.returned} data-testid="input-movement-returned"
                     onChange={(e) => setF({ ...f, returned: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Notiz</Label>
            <Input value={f.note} data-testid="input-movement-note"
                   onChange={(e) => setF({ ...f, note: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Abbrechen</Button>
          <Button onClick={save} data-testid="btn-save-movement"
                  className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
            Buchen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/* Transport dialog                                                           */
/* -------------------------------------------------------------------------- */
function TransportDialog({ initial, customers, carriers, deliveries, onClose, onSaved }) {
  const isEdit = initial.mode === "edit";
  const [f, setF] = useState({
    customer_id: initial.customer_id || "",
    delivery_id: initial.delivery_id || "",
    pickup_date: (initial.pickup_date || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
    pallet_count: initial.pallet_count || 0,
    driver_name: initial.driver_name || "",
    carrier_id: initial.carrier_id || "",
    carrier_name: initial.carrier_name || "",
    notes: initial.notes || "",
    status: initial.status || "planned",
  });
  const save = async () => {
    if (!f.customer_id) return toast.error("Kunde wählen");
    if (!f.pickup_date) return toast.error("Abholdatum setzen");
    try {
      const payload = {
        customer_id: f.customer_id,
        delivery_id: f.delivery_id || null,
        pickup_date: f.pickup_date,
        pallet_count: Number(f.pallet_count || 0),
        driver_name: f.driver_name || null,
        carrier_id: f.carrier_id || null,
        carrier_name: f.carrier_name || null,
        notes: f.notes || null,
      };
      if (isEdit) await api.patch(`/transports/${initial.id}`, { ...payload, status: f.status });
      else await api.post("/transports", payload);
      toast.success(isEdit ? "Transport aktualisiert" : "Transport angelegt");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };
  const custDeliveries = deliveries.filter((d) => d.customer_id === f.customer_id);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-transport">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {isEdit ? `Transportauftrag ${initial.transport_number}` : "Neuer Transportauftrag"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Kunde *</Label>
            <Select value={f.customer_id} onValueChange={(v) => setF({ ...f, customer_id: v, delivery_id: "" })}
                    disabled={isEdit}>
              <SelectTrigger data-testid="select-transport-customer"><SelectValue placeholder="Wählen…" /></SelectTrigger>
              <SelectContent>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Bezogene Lieferung (optional)</Label>
            <Select value={f.delivery_id || "none"} onValueChange={(v) => setF({ ...f, delivery_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="select-transport-delivery"><SelectValue placeholder="Keine…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Keine Lieferung —</SelectItem>
                {custDeliveries.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.number} · {(d.delivery_date || "").slice(0, 10)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Abholdatum *</Label>
            <Input type="date" value={f.pickup_date} data-testid="input-transport-pickup"
                   onChange={(e) => setF({ ...f, pickup_date: e.target.value })} />
          </div>
          <div>
            <Label>Anzahl Paletten</Label>
            <Input type="number" min="0" value={f.pallet_count} data-testid="input-transport-pallet-count"
                   onChange={(e) => setF({ ...f, pallet_count: e.target.value })} />
          </div>
          <div>
            <Label>Spediteur (Verzeichnis)</Label>
            <Select value={f.carrier_id || "none"}
                    onValueChange={(v) => {
                      if (v === "none") setF({ ...f, carrier_id: "", carrier_name: "" });
                      else {
                        const c = carriers.find((x) => x.id === v);
                        setF({ ...f, carrier_id: v, carrier_name: c?.name || "" });
                      }
                    }}>
              <SelectTrigger data-testid="select-transport-carrier"><SelectValue placeholder="Keiner…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Freitext / Kein Verzeichnis —</SelectItem>
                {carriers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Fahrer / Freitext</Label>
            <Input value={f.driver_name} data-testid="input-transport-driver"
                   placeholder="Name / eigener Fahrer"
                   onChange={(e) => setF({ ...f, driver_name: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Label>Notiz</Label>
            <Input value={f.notes} data-testid="input-transport-notes"
                   onChange={(e) => setF({ ...f, notes: e.target.value })} />
          </div>
          {isEdit && (
            <div className="col-span-2">
              <Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger data-testid="select-transport-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Geplant</SelectItem>
                  <SelectItem value="in_transit">Unterwegs</SelectItem>
                  <SelectItem value="completed">Abgeschlossen</SelectItem>
                  <SelectItem value="cancelled">Storniert</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-xs text-[var(--text-muted)] mt-1">
                „Abgeschlossen" bucht die {f.pallet_count} Paletten automatisch als Rückgabe im Palettenkonto.
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Abbrechen</Button>
          <Button onClick={save} data-testid="btn-save-transport"
                  className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/* Carrier dialog                                                             */
/* -------------------------------------------------------------------------- */
function CarrierDialog({ initial, onClose, onSaved }) {
  const isEdit = initial.mode === "edit";
  const [f, setF] = useState({
    name: initial.name || "",
    contact_name: initial.contact_name || "",
    phone: initial.phone || "",
    email: initial.email || "",
    notes: initial.notes || "",
  });
  const save = async () => {
    if (!f.name.trim()) return toast.error("Name ist Pflicht");
    try {
      if (isEdit) await api.patch(`/carriers/${initial.id}`, f);
      else await api.post("/carriers", f);
      toast.success("Gespeichert"); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="dialog-carrier">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {isEdit ? "Spediteur bearbeiten" : "Neuer Spediteur"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name *</Label>
            <Input value={f.name} data-testid="input-carrier-name"
                   onChange={(e) => setF({ ...f, name: e.target.value })} />
          </div>
          <div>
            <Label>Kontakt-Name</Label>
            <Input value={f.contact_name} data-testid="input-carrier-contact"
                   onChange={(e) => setF({ ...f, contact_name: e.target.value })} />
          </div>
          <div>
            <Label>Telefon</Label>
            <Input value={f.phone} data-testid="input-carrier-phone"
                   onChange={(e) => setF({ ...f, phone: e.target.value })} />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={f.email} data-testid="input-carrier-email"
                   onChange={(e) => setF({ ...f, email: e.target.value })} />
          </div>
          <div>
            <Label>Notizen</Label>
            <Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Abbrechen</Button>
          <Button onClick={save} data-testid="btn-save-carrier"
                  className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
